import {
  Typography,
  Button,
  CardContent,
  Chip,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Card,
  CardHeader
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import AddCircleOutlineOutlinedIcon from "@material-ui/icons/AddCircleOutlineOutlined";
import cns from "classnames";
import { ReferenceStoreProvider, useReferenceStore } from "./use-reference-store";
import CircularProgress from "@material-ui/core/CircularProgress";
import { ReferenceFormModal } from "./reference-form-modal";
import { Option, ReferenceDropdownMenu } from "./reference-dropdown-menu";
import React from "react";
import moment from "moment";
import { Color, useAlertDialog, WithAlertDialog } from "@bonliva-crm/ui";
import { useLocale, useUserProfile } from "@hooks";
import { ConsultantReferenceStatus, ReferenceItemFragment } from "@graphql";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import DescriptionIcon from "@material-ui/icons/Description";
import { WithCopyToClipboard } from "@common";
import { ThemeType } from "@theme";
import { ReferenceStatusModal } from "./reference-status-modal";
import { OpenInNew, MailOutline, WarningOutlined} from "@material-ui/icons";
import Alert from "@material-ui/lab/Alert";
import { ReferenceMergeModal } from "./reference-merge-modal";

type ReferenceListProps = {
  consultantId: string;
};

export function ReferenceList({ consultantId }: ReferenceListProps) {
  return (
    <ReferenceStoreProvider consultantId={consultantId}>
      <WithAlertDialog>
        <ReferenceListComponent />
      </WithAlertDialog>
    </ReferenceStoreProvider>
  );
}

function ReferenceListComponent() {
  const classes = useStyles({ progress: 0 });
  const { locale } = useLocale();
  const store = useReferenceStore();
  const alert = useAlertDialog();
  const { can } = useUserProfile();
  const { cvGranskningApproved } = store;

  const greenStatuses = [
    ConsultantReferenceStatus.Approved,
    ConsultantReferenceStatus.ApprovedWithException
  ];

  const getRefOptions = (reference: ReferenceItemFragment): Option[] => {
    const migratedLocked =
      reference.migrated && reference.document && !reference.form?.submittedAt;
    const notMigratedLocked =
      !reference.migrated && reference.document && !reference.form?.submittedAt;

    const canView =
      can("consultant-reference:read:all") && reference.document && !reference.deleted;
    const canEdit =
      can("consultant-reference:update:all") &&
      !reference.deleted &&
      !migratedLocked &&
      !notMigratedLocked;
    const canArchive = can("consultant-reference:update:all") && !reference.deleted;

    return [
      {
        label: locale.Open,
        value: "view",
        onClick: () => window.open(reference.document.PreviewURL),
        disabled: !canView,
        icon: <OpenInNew fontSize="small" color={canView ? "action" : "disabled"} />
      },
      {
        label: locale.Download,
        value: "download",
        onClick: () => window.open(reference.document.PdfURL),
        disabled: !(
          can("consultant-reference:read:all") &&
          reference.document &&
          !reference.deleted
        )
      },
      {
        label: locale.Edit,
        value: "edit",
        onClick: () =>
          store.dialog.onOpen({
            reference: reference,
            refId: reference._id
          }),
        disabled: !canEdit,
        tooltip: migratedLocked
          ? locale.EditMigratedConsultantReferenceTooltip
          : notMigratedLocked
          ? "En fil har laddats upp som ersätter referensformuläret."
          : ""
      },
      {
        label: locale.Delete,
        value: "delete",
        onClick: () =>
          alert
            .confirm({
              title: locale.AreYouSure,
              description: locale.AreYouSureDeleteConsultantReference
            })
            .then((result) => {
              if (result) {
                store.onDelete(reference._id, false);
              }
            }),
        disabled: !can("consultant-reference:delete:all")
      },
      {
        label: locale.Archive,
        value: "archive",
        onClick: () =>
          alert
            .confirm({
              title: locale.AreYouSure,
              description: ""
            })
            .then((result) => {
              if (result) {
                store.onDelete(reference._id, true);
              }
            }),
        disabled: !canArchive
      },
      {
        label: locale.Restore,
        value: "restore",
        onClick: () => store.onRestore(reference._id),
        disabled: !(can("consultant-reference:update:all") && reference.deleted)
      },
      {
        label: locale.Order,
        value: "order",
        onClick: () => store.onSendRequest(reference),
        disabled:
          !(
            can("consultant-reference:update:all") &&
            !reference.deleted &&
            !reference.document &&
            reference.status !== ConsultantReferenceStatus.RequestSentToKVK
          ) ||
          !reference.formType ||
          !cvGranskningApproved,
        tooltip: !cvGranskningApproved
          ? locale.CVIsRequired
          : !reference.formType
          ? locale.OrderConsultantReferenceTooltip
          : ""
      },
      {
        label: locale.OrderUpd,
        value: "orderUpd",
        onClick: () =>
          alert
            .confirm({
              title: "Observera att uppdateringen kommer att skriva över tidigare information referenten angav",
              description: "",
              hideCancel: true
            })
            .then((result) => {
              if (result) {
                store.onUpdRequest(reference);
              }
            }),
        disabled:
          !(
            can("consultant-reference:update:all") &&
            [
              ConsultantReferenceStatus.Approved,
              ConsultantReferenceStatus.ApprovedWithException
            ].includes(reference.status) &&
            !reference.orderedUpdateFromKvk &&
            !(reference.document && !reference.form?.submittedAt)
          ) || !cvGranskningApproved,
        tooltip: !cvGranskningApproved
          ? locale.CVIsRequired
          : reference.document && !reference.form?.submittedAt
          ? "En fil har laddats upp som ersätter referensformuläret."
          : ""
      },
      {
        label: locale.SendToRef,
        value: "sendToRef",
        onClick: () =>
          alert
            .confirm({
              title: locale.AreYouSure,
              description: ""
            })
            .then((result) => {
              if (result) {
                store.onSendToRef(reference._id);
              }
            }),
        disabled: !(
          can("consultant-reference:share:all") &&
          reference.canBeSentToRef &&
          !reference.document &&
          !reference.deleted
        ),
        tooltip: !reference.canBeSentToRef
          ? locale.SendToKvkConsultantReferenceTooltip
          : ""
      },
      {
        label: locale.ReSendToRef,
        value: "reSendToRef",
        onClick: () =>
          alert
            .confirm({
              title: locale.AreYouSure,
              description: ""
            })
            .then((result) => {
              if (result) {
                store.onReSendToRef(reference._id);
              }
            }),
        disabled: !(
          can("consultant-reference:share:all") &&
          reference.canBeReSentToRef &&
          !reference.deleted
        )
      }
    ];
  };

  const getLostRefOptions = (reference: ReferenceItemFragment): Option[] => {
    const canView =
      can("consultant-reference:read:all") && reference.document && !reference.deleted;

    return [
      {
        label: locale.Merge,
        value: "merge",
        onClick: () => store.mergeDialog.onOpen({ reference }),
        disabled: !(can("consultant-reference:approve:all") && !reference.deleted)
      },
      {
        label: locale.Open,
        value: "view",
        onClick: () => window.open(reference.document.PreviewURL),
        disabled: !canView,
        icon: <OpenInNew fontSize="small" color={canView ? "action" : "disabled"} />
      },
      {
        label: locale.Download,
        value: "download",
        onClick: () => window.open(reference.document.PdfURL),
        disabled: !(
          can("consultant-reference:read:all") &&
          reference.document &&
          !reference.deleted
        )
      },
      {
        label: locale.Delete,
        value: "delete",
        onClick: () =>
          alert
            .confirm({
              title: locale.AreYouSure,
              description: locale.AreYouSureDeleteConsultantReference
            })
            .then((result) => {
              if (result) {
                store.onDelete(reference._id, false);
              }
            }),
        disabled: !(can("consultant-reference:delete:all") && !reference.deleted)
      }
    ];
  };

  const mainInfo = (reference: ReferenceItemFragment) => (
    <CardContent className={classes.content}>
      <div className={classes.bulletListWrap}>
        {reference.workPlace && (
          <Typography variant="body1" component="p">
            {reference.workPlace}
          </Typography>
        )}
        <Typography variant="body1" component="p">
          {reference.position}
        </Typography>
        {reference.orderedUpdateFromKvk && (
          <Tooltip title={"Beställd uppdatering."}>
            <Typography variant="body1" component="p" style={{ color: "orange" }}>
              <WarningOutlined color={"inherit"} />
            </Typography>
          </Tooltip>
        )}
      </div>

      {(reference.phone || reference.email) && (
        <Typography
          style={{ display: "flex", alignItems: "center" }}
          onClick={(e) => e.stopPropagation()}
        >
          {reference.phone && (
            <WithCopyToClipboard contentToCopy={reference.phone}>
              <a className={classes.link} href={`tel:${reference.phone}`}>
                {reference.phone}
              </a>
            </WithCopyToClipboard>
          )}
          {reference.email && (
            <WithCopyToClipboard contentToCopy={reference.email}>
              <a className={classes.link} href={`mailto:${reference.email}`}>
                {reference.email}
              </a>
            </WithCopyToClipboard>
          )}
        </Typography>
      )}
      <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
        <Typography
          className={classes.bulletListWrap}
          style={{ fontSize: 14 }}
          variant="body1"
          component="p"
        >
          {reference.formType && (
            <span>{locale.consultantReferenceType[reference.formType]}</span>
          )}
          {reference.lastStatusHistory && (
            <>
              <span className={classes.semibold}>
                {locale.consultantReferenceStatus[reference.lastStatusHistory?.newStatus]}
              </span>
              <span>
                {"Status uppdaterad av "}
                {reference.lastStatusHistory?.changedBy?.name}
                {", "}
                {moment(reference.lastStatusHistory?.changedAt).format("L")}
              </span>
            </>
          )}
        </Typography>
        {!reference.document &&
          reference.name &&
          greenStatuses.includes(reference.status) && (
            <Tooltip title={"Dokument saknas"}>
              <DescriptionIcon
                fontSize={"small"}
                className={cns(classes.infoIcon, classes.red)}
              />
            </Tooltip>
          )}
        {reference.document && !reference.form?.submittedAt && (
          <Tooltip title={"En fil har laddats upp som ersätter referensformuläret."}>
            <DescriptionIcon
              fontSize={"small"}
              className={cns(classes.infoIcon, classes.green)}
            />
          </Tooltip>
        )}
      </div>

      {reference.reminders
        ?.filter((_, i) => i <= 1)
        .map((reminder) => (
          <Tooltip title={reminder.sentTo ?? ""} placement="top">
            <Typography variant={"caption"}>
              {`Påmind: ${moment.utc(reminder.createdAt).format("L HH:mm")}`}
            </Typography>
          </Tooltip>
        ))}
      {reference.form?.submittedAt && (
        <Tooltip
          title={
            reference.submissionHistory?.map(
              (x) => `${x.submittedBy} ${moment(x.submittedAt).format("L")}`
            ) ?? ""
          }
          placement="top"
        >
          <Typography variant={"caption"} style={{ borderTop: "1px solid #C0C0C0" }}>
            Formulär redigerat av {reference.form.submittedBy?.name || reference.name}{" "}
            {moment(reference.form?.submittedAt).format("L")}
          </Typography>
        </Tooltip>
      )}
    </CardContent>
  );

  const references: [ReferenceItemFragment, JSX.Element][] = store.references.map(
    (reference) => [
      reference,
      <Card
        key={reference._id}
        raised
        className={cns(classes.itemWrap, {
          [classes.success]: greenStatuses.includes(reference.status),
          [classes.error]: reference.status === ConsultantReferenceStatus.NotApproved,
          [classes.deleted]: reference.deleted
        })}
        onClick={() => {
          if (reference.deleted) {
            return;
          }

          return can("consultant-reference:approve:all")
            ? store.statusDialog.onOpen({ reference })
            : null;
        }}
      >
        <CardHeader
          className={classes.cardHeader}
          title={
            <div className={classes.headRow}>
              <Typography variant={"h4"}>
                {reference.name}
              </Typography>
              {reference.workRelation && (
                <Chip
                  size={"small"}
                  label={locale.consultantReferenceRelation[reference.workRelation]}
                  title={locale.consultantReferenceRelation[reference.workRelation]}
                />
              )}
              {reference.reShared && (
                <Tooltip title={locale.ReSentToRef}>
                  <MailOutline />
                </Tooltip>
              )}
              <ReferenceDropdownMenu options={getRefOptions(reference)} />
            </div>
          }
        />
        {mainInfo(reference)}
      </Card>
    ]
  );

  const activeReferences = [];
  const deletedReferences = [];

  for (const tuple of references) {
    const [reference, element] = tuple;

    if (reference.deleted) {
      deletedReferences.push(element);
    } else {
      activeReferences.push(element);
    }
  }

  return (
    <div className={classes.wrapper}>
      {store.dialog.open && ( // hacky way to set initial values
        <ReferenceFormModal
          reference={store.dialog?.data?.reference}
          open={store.dialog.open}
          setOpen={() => store.dialog.onClose()}
          onSubmit={store.onSubmit}
          areasOfExpertise={store.areasOfExpertise}
          onSendRequest={(data) => store.onSendRequest(data)}
          loading={store.submitting}
          cvGranskningApproved={cvGranskningApproved}
        />
      )}
      {store.statusDialog.open && (
        <ReferenceStatusModal
          open={store.statusDialog.open}
          data={store.statusDialog.data.reference}
          references={store.references}
          setOpen={() => store.statusDialog.onClose()}
          loading={store.submitting}
          onSubmit={(refId, input, file) => store.onChangeStatus(refId, input, file)}
        />
      )}
      {store.mergeDialog.open && (
        <ReferenceMergeModal
          open={store.mergeDialog.open}
          references={store.references.filter(
            (x) => x.status === ConsultantReferenceStatus.Empty
          )}
          data={store.mergeDialog.data.reference}
          setOpen={() => store.mergeDialog.onClose()}
          loading={store.submitting}
          onSubmit={(refId, mergeWithRefId) =>
            store.onMergeReference(refId, mergeWithRefId)
          }
        />
      )}

      <div className={classes.titleWrap}>
        <Typography variant="h6">{locale.ReferencesAssignmentEvaluations}</Typography>
        <Tooltip title={!cvGranskningApproved ? locale.CVIsRequired : ""}>
          <span>
            <Button
              variant={"outlined"}
              color={"primary"}
              startIcon={<AddCircleOutlineOutlinedIcon />}
              onClick={() => store.dialog.onOpen(null)}
              disabled={!cvGranskningApproved}
            >
              {locale.newForm}
            </Button>
          </span>
        </Tooltip>
      </div>

      {store.loading && (
        <Box
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          <CircularProgress size={32} />
        </Box>
      )}
      <div className={classes.itemsContainer}>{activeReferences}</div>
      <br />

      {deletedReferences.length > 0 && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <div className={""}>
              <Typography variant="h6">{locale.Archived}</Typography>
            </div>
          </AccordionSummary>
          <AccordionDetails>
            <div className={classes.itemsContainer}>{deletedReferences}</div>
          </AccordionDetails>
        </Accordion>
      )}
      <br />

      {store.lostReferences.length > 0 && (
        <>
          <Alert severity="warning">
            <Typography variant="h6">{locale.QualityStatusesWithoutForms}</Typography>
          </Alert>
          <br />
          <div className={classes.itemsContainer}>
            {store.lostReferences.map((reference) => (
              <Card
                key={reference._id}
                raised
                className={cns(classes.itemWrap, {
                  [classes.success]: greenStatuses.includes(reference.status),
                  [classes.error]:
                    reference.status === ConsultantReferenceStatus.NotApproved
                })}
              >
                <CardHeader
                  className={classes.cardHeader}
                  title={
                    <div className={classes.headRow}>
                      {reference.name && (
                        <Typography variant={"h4"}>
                          {reference.name}
                        </Typography>
                      )}
                      {reference.workRelation && (
                        <Chip
                          size={"small"}
                          label={
                            locale.consultantReferenceRelation[reference.workRelation]
                          }
                          title={
                            locale.consultantReferenceRelation[reference.workRelation]
                          }
                        />
                      )}
                      <ReferenceDropdownMenu options={getLostRefOptions(reference)} />
                    </div>
                  }
                />
                {mainInfo(reference)}
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

const useStyles = makeStyles((theme: ThemeType) => ({
  wrapper: {
    padding: "4px 7px"
  },
  itemsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    width: "100%"
  },
  titleWrap: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(3)
  },
  lostTitleWrap: {
    padding: `${theme.spacing(3)}px 0`
  },
  headRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1)
  },
  bulletListWrap: {
    display: "flex",
    flexWrap: "wrap",
    "& > *:not(:last-child):after": {
      content: "'•'",
      margin: `0 ${theme.spacing(0.7)}px`
    }
  },
  content: {
    display: "flex",
    gap: theme.spacing(0.5),
    flexDirection: "column",
    padding: `0 ${theme.spacing(2)}px`,
    alignItems: "flex-start"
  },
  itemWrap: {
    cursor: "pointer",
    padding: `0 ${theme.spacing(2)}px`
  },
  statusWrap: {
    fontWeight: 500,
    position: "relative"
  },
  badge: {
    position: "absolute",
    left: -25
  },
  success: {
    background: "#F3FDEE"
  },
  error: {
    background: "#FFEBE6"
  },
  deleted: {
    background: "#E0E8E880",
    "& *": {
      color: "#00000080 !important"
    },
    "& a": {
      color: "#ff867b99 !important"
    }
  },
  semibold: {
    fontWeight: 500
  },
  link: {
    color: theme.palette.primary.main
  },
  cardHeader: {
    padding: `${theme.spacing(2)}px ${theme.spacing(2)}px 0`
  },
  infoIcon: {
    margin: "0 5px 0 auto"
  },
  green: {
    color: "rgb(76, 217, 100)"
  },
  red: {
    color: Color.PrimaryMain
  }
}));
